import React, { useState } from 'react';
import { ScrollArea } from '../components/ui/scroll-area';
import { Trash2, Clock, FileText, BookMarked, Search, X, ThumbsUp, ThumbsDown, Layers, Library, Crown } from 'lucide-react';

function formatDate(dateStr) {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' });
  } catch {
    return '';
  }
}

export default function Sidebar({ items, loading, onSelect, onDelete, isOpen, onClose }) {
  const [search, setSearch] = useState('');

  const filtered = search.trim()
    ? items.filter(i => i.topic?.toLowerCase().includes(search.toLowerCase()))
    : items;

  return (
    <>
      {/* Fondo oscuro para móvil cuando el menú está abierto */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/60 z-40 lg:hidden backdrop-blur-sm transition-opacity"
          onClick={onClose}
        />
      )}

      {/* El Sidebar en sí */}
      <aside 
        className={`fixed lg:static inset-y-0 left-0 z-50 w-72 lg:w-80 flex-shrink-0 border-r border-slate-800 bg-slate-950 flex flex-col transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        {/* Header */}
        <div className="h-14 flex items-center gap-3 px-4 border-b border-slate-800 flex-shrink-0 justify-between">
          <div className="flex items-center gap-2">
            <Library size={16} className="text-blue-400 flex-shrink-0" strokeWidth={2} />
            <h2 className="text-xs sm:text-sm font-bold text-slate-200 tracking-wide truncate">Mi biblioteca de clases preparadas</h2>
          </div>
          
          <div className="flex items-center gap-2">
            <span className="bg-slate-800 text-slate-400 text-[10px] font-bold px-2 py-0.5 rounded-full">
              {filtered.length}{search ? `/${items.length}` : ''}
            </span>
            
            <button 
              onClick={onClose}
              className="lg:hidden p-1.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="px-4 py-3 border-b border-slate-800 flex-shrink-0">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
            <input
              data-testid="library-search-input"
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Buscar clase..."
              className="w-full bg-slate-900 border border-slate-800 hover:border-slate-700 focus:border-blue-500/50 text-slate-200 placeholder-slate-500 text-xs font-medium rounded-xl pl-9 pr-8 py-2.5 outline-none transition-all shadow-inner"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-500 hover:text-slate-300 hover:bg-slate-800 rounded-md transition-colors"
                data-testid="clear-search-btn"
              >
                <X size={12} strokeWidth={2.5} />
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <ScrollArea className="flex-1">
          <div className="p-3 space-y-2">
            {loading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-20 bg-slate-900 rounded-2xl animate-pulse border border-slate-800/50" />
              ))
            ) : filtered.length === 0 ? (
              <div className="p-6 text-center mt-6">
                <div className="w-12 h-12 bg-slate-900 rounded-full flex items-center justify-center mx-auto mb-4 border border-slate-800">
                  <FileText size={20} className="text-slate-600" strokeWidth={1.5} />
                </div>
                <p className="text-slate-500 text-xs font-medium leading-relaxed">
                  {search ? `Sin resultados para "${search}"` : 'Tu biblioteca está vacía.\nGenerá y guardá dinámicas.'}
                </p>
              </div>
            ) : (
              filtered.map(item => {
                const isMasterSequence = item.is_master_sequence;
                let sessionCount = 1;
                
                if (isMasterSequence) {
                    sessionCount = Array.isArray(item.cards) ? item.cards.length : (item.cards?.semanas?.length || item.cards?.clases?.length || 1);
                } else if (item.cards && item.cards.clases && Array.isArray(item.cards.clases)) {
                    sessionCount = item.cards.clases.length;
                } else if (Array.isArray(item.cards)) {
                    sessionCount = item.cards.length;
                }

                return (
                  <div
                    key={item.id}
                    data-testid={`history-item-${item.id}`}
                    className={`group relative bg-slate-900 hover:bg-slate-800 border rounded-2xl p-4 cursor-pointer transition-all hover:shadow-lg ${isMasterSequence ? 'border-amber-500/30 hover:border-amber-500/50' : 'border-slate-800 hover:border-slate-700'}`}
                    onClick={() => {
                      onSelect(item);
                      if (onClose) onClose(); 
                    }}
                  >
                    <p className={`text-xs font-bold line-clamp-2 pr-6 leading-relaxed ${isMasterSequence ? 'text-amber-100' : 'text-slate-200'}`}>
                      {item.topic}
                    </p>
                    
                    <div className="flex items-center gap-2.5 mt-2.5 flex-wrap">
                      <span className="bg-slate-950 border border-slate-800 text-slate-400 text-[10px] font-bold px-2 py-0.5 rounded-md truncate max-w-[80px]">
                        {item.age}
                      </span>

                      {isMasterSequence ? (
                          <span className="flex items-center gap-1 bg-amber-500/10 border border-amber-500/20 text-amber-400 text-[10px] font-bold px-1.5 py-0.5 rounded-md">
                              <Crown size={10} /> SECUENCIA ({sessionCount})
                          </span>
                      ) : sessionCount > 1 ? (
                          <span className="flex items-center gap-1 bg-violet-500/10 border border-violet-500/20 text-violet-400 text-[10px] font-bold px-1.5 py-0.5 rounded-md">
                              <Layers size={10} /> {sessionCount}
                          </span>
                      ) : null}

                      <div className="flex items-center gap-1 text-slate-500">
                        <Clock size={10} strokeWidth={2.5} />
                        <span className="text-[9px] font-bold tracking-wide uppercase">{formatDate(item.created_at)}</span>
                      </div>

                      {item.rating === 'up' && <ThumbsUp size={12} className="text-emerald-500 ml-auto" />}
                      {item.rating === 'down' && <ThumbsDown size={12} className="text-red-400 ml-auto" />}
                    </div>
                    
                    <button
                      data-testid={`delete-history-${item.id}`}
                      onClick={e => { e.stopPropagation(); onDelete(item.id); }}
                      className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 p-1.5 bg-slate-950 text-slate-500 hover:text-red-400 hover:bg-red-500/10 border border-slate-800 transition-all rounded-lg"
                    >
                      <Trash2 size={12} strokeWidth={2} />
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </ScrollArea>

        {/* Footer */}
        <div className="p-4 border-t border-slate-800 bg-slate-950/50">
          <p className="text-[10px] font-bold text-slate-600 text-center uppercase tracking-widest">
            Guardar dinámica: 1 crédito
          </p>
        </div>
      </aside>
    </>
  );
}
